import { assembleWorkout, type LogSet, type Session, type Workout } from "@heraion/shared";

import type { DurableWriteFn, DurableWriteResult } from "../seams";

type SessionDetail = { session: Session; sets: LogSet[] };

/** #368: the diary's reads and deletes, one copy for both hosts. Each host
 *  keeps its own fetch (mobile reads through its cache), its error words
 *  and what it evicts. */

/** Deletes `ids` one after another and stops at the first refusal. `sent`
 *  and `queued` both count as gone: on mobile a queued delete is durable. */
export async function deleteSessionsInOrder(
  durableWrite: DurableWriteFn,
  ids: readonly string[],
): Promise<{ gone: string[]; refused: DurableWriteResult | null }> {
  const gone: string[] = [];
  for (const id of ids) {
    const result = await durableWrite({ method: "DELETE", path: `/api/sessions/${encodeURIComponent(id)}` });
    if (result.outcome === "rejected") return { gone, refused: result };
    gone.push(id);
  }
  return { gone, refused: null };
}

/** One session with its sets, through the host's read. Throws when there is
 *  none, so the caller says it could not be read. */
export const workoutReader =
  (fetchDetail: (id: string) => Promise<SessionDetail | null>) =>
  async (id: string): Promise<Workout> => {
    const res = await fetchDetail(id);
    if (!res) throw new Error("no session");
    return assembleWorkout(res.session, res.sets);
  };
